import {Component, OnInit, Input} from "@angular/core";
import {Invoice} from "../../../shared/models/invoice";
import {FormGroup, FormControl, Validators} from "@angular/forms";
import {ExamCenter} from "../../../shared/models/examCenter";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {InvoiceService} from "../../../shared/services/invoice/invoice.service";
import {ExamCenterService} from "../../../shared/services/exam-center/exam-center.service";
import {VoucherRequestService} from "../../../shared/services/voucher-request/voucher-request.service";
import {VoucherRequest} from "../../../shared/models/voucherRequest";
import {forkJoin} from "rxjs";
import {take} from "rxjs/operators";
import {FileDownloadHelper} from "../../../shared/helpers/file-download.helper";
import {HttpResponse} from "@angular/common/http";
import {ToastrService} from "ngx-toastr";


/*
 * Ce qu'on affiche d'une demande dans le sélecteur, quelle que soit sa source :
 * les demandes en attente viennent de l'API demandes, celle déjà rattachée à la
 * facture vient de la facture elle-même.
 */
interface VoucherRequestOption {
    id: number;
    quantity: number;
    created_date: Date; // eslint-disable-line camelcase
}


@Component({
    selector: "app-invoice-modal",
    templateUrl: "./invoice-modal.component.html",
    styleUrls: ["./invoice-modal.component.scss"]
})
export class InvoiceModalComponent implements OnInit {

    @Input() invoice: Invoice;

    invoiceForm: FormGroup;
    examCenters: Array<ExamCenter>;
    isDataLoading: boolean = true;
    now: Date;
    document: File;
    submitting: boolean = false;
    retrievingDocument: boolean = false;

    // Toutes les demandes encore en attente, tous centres confondus.
    pendingRequests: Array<VoucherRequest> = [];

    // Sous-ensemble proposé pour le centre choisi.
    requestOptions: Array<VoucherRequestOption> = [];

    constructor (
        public activeModal: NgbActiveModal,
        public examCenterService: ExamCenterService,
        public invoiceService: InvoiceService,
        private voucherRequestService: VoucherRequestService,
        private toastr: ToastrService
    ) {
        this.invoiceForm = new FormGroup({
            label: new FormControl(""),
            center: new FormControl(
                "",
                Validators.required
            ),
            date: new FormControl(""),

            // Rattachement facultatif à une demande de tickets.
            voucherRequest: new FormControl("")
        });
        this.isDataLoading = true;
        this.now = new Date();
    }

    /*
     * Function to instanciate the frontend form
     * Call only after all data is fetched
     */
    private resetForm () {

        // Populate the form if there's an input
        if (this.invoice !== null) {
            this.invoiceForm.setValue({
                label: this.invoice.label,
                center: this.invoice.center
                    ? this.invoice.center.id
                    : "",
                date: this.invoice.date,
                voucherRequest: this.attachedRequest
                    ? this.attachedRequest.id
                    : ""
            });
        }
    }

    /* La demande déjà rattachée à la facture ouverte, s'il y en a une. */
    private get attachedRequest (): VoucherRequestOption {
        return this.invoice?.voucher_requests?.length
            ? this.invoice.voucher_requests[0]
            : null;
    }

    /*
     * Les demandes proposées sont celles encore en attente sur le centre choisi.
     * En édition, la demande déjà rattachée s'y ajoute : elle est confirmée,
     * donc absente de la liste des demandes en attente.
     */
    private refreshRequestOptions (centerValue: any = this.invoiceForm.value.center) {
        /*
         * La valeur vient de `valueChanges`, pas de `invoiceForm.value` : un
         * contrôle émet avant que le groupe parent ait recalculé sa valeur, on
         * lirait donc le centre précédent.
         */
        const centerId = Number(centerValue);

        const options: Array<VoucherRequestOption> = this.pendingRequests.
            filter((request: VoucherRequest) => request.exam_center?.id === centerId).
            map((request: VoucherRequest) => ({id: request.id,
                quantity: request.quantity,
                created_date: request.created_date}));

        const attached = this.attachedRequest;
        if (attached && this.invoice?.center?.id === centerId) {
            options.unshift(attached);
        }

        this.requestOptions = options;

        const control = this.invoiceForm.get("voucherRequest");
        const selected = control.value
            ? Number(control.value)
            : null;

        // Le centre a changé : la demande retenue n'est plus proposable.
        if (selected !== null && !options.some((option) => option.id === selected)) {
            control.setValue("");
        }

        /*
         * Présélection à la création seulement : sans ambiguïté, on évite au
         * staff un choix. En édition, elle rattacherait une demande à une
         * facture qu'on ouvrait pour tout autre chose.
         */
        if (!this.invoice && !control.value && options.length === 1) {
            control.setValue(options[0].id);
        }
    }

    /*
     * Une seule demande en attente, tous centres confondus : à la création, on
     * pose son centre pour qu'elle soit proposée d'emblée. Le staff n'ouvre pas
     * la modale pour autre chose, et il reste libre de changer de centre.
     */
    private preselectLoneRequest () {
        if (this.invoice || this.pendingRequests.length !== 1) {
            return;
        }

        const request = this.pendingRequests[0];
        if (request.exam_center) {
            this.invoiceForm.get("center").setValue(request.exam_center.id);
        }
    }

    ngOnInit () {
        forkJoin(
            this.examCenterService.list(true).pipe(take(1)),
            this.voucherRequestService.list({status: "pending"}).pipe(take(1))
        ).pipe(take(1)).
            subscribe(([
                examCenters,
                pendingRequests
            ]) => {
                this.examCenters = examCenters;
                this.pendingRequests = pendingRequests;
                this.resetForm();
                this.preselectLoneRequest();
                this.refreshRequestOptions();
                this.isDataLoading = false;

                // Les demandes dépendent du centre : on les recalcule à chaque
                // changement.
                this.invoiceForm.get("center").valueChanges.subscribe((centerId: string) => {
                    this.refreshRequestOptions(centerId);
                });
            });
    }

    onFileChange (event) {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];
            this.document = file;
        }
    }

    validate () {
        this.submitting = true;

        const submitInvoice = new Invoice(this.invoice);
        submitInvoice.label = this.invoiceForm.value.label;
        submitInvoice.center = this.examCenters.find((ec: ExamCenter) => ec.id == this.invoiceForm.value.center);
        submitInvoice.date = this.invoiceForm.value.date;

        const voucherRequestId = this.invoiceForm.value.voucherRequest
            ? Number(this.invoiceForm.value.voucherRequest)
            : null;

        // Trigger update if it's an edit
        if (this.invoice) {
            this.invoiceService.update(
                submitInvoice,
                voucherRequestId
            ).pipe(take(1)).
                subscribe(
                    (res: Invoice) => {
                        this.invoice = res;

                        // If there's a document to upload, upload it then put it back in the invoice object
                        if (this.document) {
                            this.invoiceService.uploadFile(
                                this.invoice.id,
                                this.document
                            ).pipe(take(1)).
                                subscribe((res2: string) => {
                                    this.invoice.document = res2;
                                    this.resetForm();
                                    this.activeModal.close(this.invoice);
                                });
                        } else {
                            this.resetForm();
                            this.activeModal.close(this.invoice);
                        }
                    },
                    (err) => {
                        this.toastr.error(
                            this.voucherRequestError(err) ?? "Une erreur est survenue",
                            "Erreur"
                        );
                        this.submitting = false;
                        throw err;
                    }
                );
        } else {
            this.invoiceService.create(
                submitInvoice,
                voucherRequestId
            ).pipe(take(1)).
                subscribe(
                    (res: Invoice) => {
                        this.invoice = res;

                        // If there's a document to upload, upload it then put it back in the invoice object
                        if (this.document) {
                            this.invoiceService.uploadFile(
                                this.invoice.id,
                                this.document
                            ).pipe(take(1)).
                                subscribe((res2: string) => {
                                    this.invoice.document = res2;
                                    this.resetForm();
                                    this.activeModal.close(this.invoice);
                                });
                        } else {
                            this.resetForm();
                            this.activeModal.close(this.invoice);
                        }
                    },
                    (err) => {
                        if (err.status === 597) {
                            this.toastr.error(
                                "Attention ! l'email de confirmation n'a pas pu être envoyé",
                                "Opération enregistrée"
                            );
                        } else {
                            this.toastr.error(
                                this.voucherRequestError(err) ?? "Une erreur est survenue",
                                "Erreur"
                            );
                        }

                        this.submitting = false;
                        throw err;
                    }
                );
        }
    }

    /*
     * Le rattachement peut être refusé par le back (demande déjà traitée entre
     * l'ouverture de la modale et la validation) : ce cas mérite mieux qu'un
     * message générique.
     */
    private voucherRequestError (err: any): string {
        return err?.error?.voucher_request_id ?? null;
    }

    showDocument () {
        this.retrievingDocument = true;

        this.invoiceService.retrieveFile(this.invoice.id).pipe(take(1)).
            subscribe(
                (response: HttpResponse<Blob>) => {
                    this.retrievingDocument = false;
                    FileDownloadHelper.downloadBlobFromResponse(response);
                },
                () => {
                    this.retrievingDocument = false;
                    this.toastr.error(
                        "Contactez l'administrateur pour plus d'informations",
                        "Fichier introuvable"
                    );
                }
            );
    }
}
